import { useState, useMemo, useEffect } from "react";
import { CHALLENGE_ITEM_TYPES, CHALLENGE_ITEM_ORDER } from "constants/index";

/**
 * useChallengeModal
 *
 * @param {Object}  challengeDetails - The details of the challenge, including its items.
 * @param {boolean} show - Whether the challenge modal is currently shown.
 * @returns {Object} - The state and handlers for the challenge modal.
 */
export default function useChallengeModal(challengeDetails, show) {
  const { challengeItems = [] } = challengeDetails || {};

  const [activeItemIndex, setActiveItemIndex] = useState(0);
  const [collapsedGroups, setCollapsedGroups] = useState({
    coding: false,
    mcq: false,
    video: false,
  });

  // Reset indices and collapse states when modal is mounted or data shifts
  useEffect(() => {
    if (show) {
      setActiveItemIndex(0);
      setCollapsedGroups({ coding: false, mcq: false, video: false });
    }
  }, [show, challengeDetails]);

  // Sequentially sorted: Coding -> MCQ -> Open Ended
  const sortedChallengeItems = useMemo(() => {
    return [...challengeItems].sort((a, b) => {
      const indexA = CHALLENGE_ITEM_ORDER.indexOf(a.type);
      const indexB = CHALLENGE_ITEM_ORDER.indexOf(b.type);
      const orderA = indexA === -1 ? 99 : indexA;
      const orderB = indexB === -1 ? 99 : indexB;
      return orderA - orderB;
    });
  }, [challengeItems]);

  // Aggregate item stats
  const stats = useMemo(() => {
    const counts = { coding: 0, mcq: 0, video: 0 };
    sortedChallengeItems.forEach((item) => {
      if (item.type === CHALLENGE_ITEM_TYPES.CODING) counts.coding += 1;
      else if (item.type === CHALLENGE_ITEM_TYPES.MCQ) counts.mcq += 1;
      else if (item.type === CHALLENGE_ITEM_TYPES.OPEN_ENDED) counts.video += 1;
    });
    return counts;
  }, [sortedChallengeItems]);

  // Group tasks for sidebar tracking maintaining their globally-sorted indexes
  const categorizedTasks = useMemo(() => {
    const coding = [];
    const mcq = [];
    const video = [];

    sortedChallengeItems.forEach((item, index) => {
      const taskWithGlobalIndex = { ...item, originalIndex: index };
      if (item.type === CHALLENGE_ITEM_TYPES.CODING) coding.push(taskWithGlobalIndex);
      else if (item.type === CHALLENGE_ITEM_TYPES.MCQ) mcq.push(taskWithGlobalIndex);
      else if (item.type === CHALLENGE_ITEM_TYPES.OPEN_ENDED) video.push(taskWithGlobalIndex);
    });

    return { coding, mcq, video };
  }, [sortedChallengeItems]);

  const activeItem = sortedChallengeItems[activeItemIndex];

  const handlePrev = () => {
    if (activeItemIndex > 0) setActiveItemIndex(activeItemIndex - 1);
  };

  const handleNext = () => {
    if (activeItemIndex < sortedChallengeItems.length - 1) setActiveItemIndex(activeItemIndex + 1);
  };

  const toggleGroup = (group) => {
    setCollapsedGroups((prev) => ({ ...prev, [group]: !prev[group] }));
  };

  return {
    sortedChallengeItems,
    activeItemIndex,
    setActiveItemIndex,
    activeItem,
    collapsedGroups,
    stats,
    categorizedTasks,
    toggleGroup,
    handlePrev,
    handleNext,
  };
}
